import React, { ChangeEvent, useCallback } from 'react';
import { Trans } from 'react-i18next';
import { Checkbox as AlphaCheckBox, Text } from '@oneaudi/unified-web-alpha-components';
import CheckIcon from '../../../assets/icons/CheckMarkIcon';
import { COLORS } from '../../../styles/variables';
import { CheckboxWrapper, Label, Checkbox, HiddenInput, SubLabel } from './CheckBoxInput.styles';
import { Url } from '../../LegalDisclaimer/LegalDisclaimer.styles';

interface CheckBoxProps {
  inputName: string;
  checked: boolean;
  label: string | React.ReactNode;
  subLabel?: string;
  ariaLabel?: string;
  isLegal?: boolean;
  transComponents?: Record<string, React.ReactElement>;
  onChange: (inputName: string, value: boolean) => void;
}

const CheckBox: React.FC<CheckBoxProps> = ({
  inputName,
  checked,
  onChange,
  label,
  subLabel,
  ariaLabel,
  isLegal,
  transComponents,
}) => {
  const handleChange = useCallback(() => {
    onChange(inputName, !checked);
  }, [checked, inputName, onChange]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== ' ') return;
      e.preventDefault();
      handleChange();
    },
    [handleChange],
  );

  if (!process.env.IS_LEGACY) {
    return (
      <CheckboxWrapper>
        <AlphaCheckBox
          hint={subLabel}
          id={inputName}
          label={
            typeof label === 'string' ? (
              <Text
                tag="p"
                variant={{
                  type: 'copy',
                  order: '3',
                  style: 'normal',
                }}
              >
                <Trans i18nKey={label} components={transComponents} />
              </Text>
            ) : (
              label
            )
          }
          checked={checked}
          value={inputName}
          aria-label={ariaLabel}
          aria-checked={checked}
          onKeyDown={handleKeyPress}
          onChange={handleChange}
          name={inputName}
          data-testid={`${inputName}-checkbox`}
        />
      </CheckboxWrapper>
    );
  }

  return (
    <CheckboxWrapper>
      <Checkbox
        tabIndex={0}
        onClick={handleChange}
        role="checkbox"
        aria-label={ariaLabel}
        aria-checked={checked}
        onKeyDown={handleKeyPress}
        checked={checked}
        data-testid="dl-legal-disclaimer-checkbox"
      >
        {checked && <CheckIcon color={COLORS.black} />}
      </Checkbox>
      <HiddenInput
        id={inputName}
        type="checkbox"
        onChange={handleChange}
        checked={checked}
        name={inputName}
      />
      <Label htmlFor={inputName} isLegal={isLegal}>
        {typeof label === 'string' ? <Trans i18nKey={label} components={transComponents} /> : label}
      </Label>
      {subLabel !== '' && <SubLabel htmlFor={inputName}>{subLabel}</SubLabel>}
    </CheckboxWrapper>
  );
};

export default CheckBox;
